import axios from "axios";
import { useEffect, useState } from "react";

/**
 * Function to fetch data using Axios.
 * @param {import("axios").AxiosRequestConfig} request Axios request config
 * @returns
 */
const fetchData = async (request) => {
  try {
    const data = await axios.request(request);
    return data;
  } catch (e) {
    throw e;
  }
};

/**
 * Transform the configuration object to a URL with query parameters.
 * @param {object} config The configuration object for the API request including filters and sort.
 * @returns {URL} The URL object with the query string.
 */
const transformConfigToUrl = (config) => {
  const query = new URLSearchParams("");

  const { presetFilters, fields, facets, filters, sort, host, q } = config;

  /**
   * Rewrite preset filters to url filters
   * @example field_key=filter_value&operator[0]=gte
   */
  if (presetFilters) {
    presetFilters.forEach((presetFilter) => {
      query.set(presetFilter.field, presetFilter.value);
      query.set(`operator[${presetFilter.field}]`, presetFilter.operator);
    });
  }

  /**
   * Rewrite field filters to url filters
   * @example field_key=filter_value
   */
  if (filters && fields) {
    fields.forEach((field) => {
      if (filters[field.id]?.length > 0) {
        query.set(field.id, filters[field.id].join(","));
      }
    });
  }

  /**
   * Rewrite facet filters to url filters
   * @example f[0]=filter_key:filter_value
   */
  if (filters && facets) {
    let facetCount = 0;

    facets.forEach((facet) => {
      const facetId = facet.id;
      if (filters[facetId]?.length > 0) {
        filters[facetId].sort();
        filters[facetId].forEach((filterValue) => {
          const key = `f[${facetCount++}]`;
          const value = `${facetId}:${filterValue}`;
          query.set(key, value);
        });
      }
    });
  }

  /**
   * Rewrite sort to url filters
   * @example sort=sortkey&order=asc
   */
  if (sort) {
    const sortParams = sort.split(":");
    query.set("sort", sortParams[0]);
    query.set("order", sortParams[1]);
  }
  query.set("sort", "year_max");
  query.set("order", "desc");

  /**
   * Rewrite sort to url filters
   * @example sort=sortkey&order=asc
   */
  if (q) {
    query.set("q", q.replaceAll("ä", "ae").replaceAll("ö", "oe").replaceAll("ü", "ue").replaceAll("ß", "ss"));
  }

  /* if (pagerSize && pagerSize > 0) {
    if (!config.limits.includes(pagerSize)) {
      console.error(
        `Invalid pager size ${pagerSize}. Allowed values are ${config.limits.join(
          ", "
        )}`
      );
    } else if (pagerSize !== config.pagerSize) {
      query.set("limit", pagerSize);
    }
  } */

  /**
   * Set the current page
   * @example page=0
   */
  query.set("page", config.page);

  /**
   * Finally, build the URL and return it
   */
  const queryString = query.toString();
  const url = new URL(host);
  url.search = queryString;

  return url;
};

/**
 * React hook to simplify the handling of Axios requests.
 * @param {import("axios").AxiosRequestConfig} request Axios request config
 * @returns {{
 *  data: object,
 *  loading: boolean,
 *  error: any,
 *  fetchMore: function
 * }} The first element is an object with the response data,
 */
const useSearchAPIRequest = (config) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const url = transformConfigToUrl(config);

  /**
   * Perform the Axios request when the component mounts or when the request changes.
   * The request is serialized to ensure that the effect only runs when the request changes.
   */
  useEffect(() => {
    setLoading(true);
    setData(prev => ({
      ...prev,
      search_results: [],
    }));
    fetchData({
      method: "GET",
      url: url.toString(),
    })
      .then((response) => {
        setData(response.data);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [JSON.stringify(config)]);

  /**
   * Function to fetch more data using the Axios request.
   * @param {function} updateRequest - Function to update the request with the new page number
   * @param {function} updateData - Function to update the data with the new response
   */
  const fetchMore = ({ updateConfig = null, updateData = null }) => {
    if (!updateConfig) {
      throw new Error("No updateConfig function provided");
    }
    if (typeof updateConfig !== "function") {
      throw new TypeError(
        `updateConfig is of type ${typeof updateData} but should be a function`
      );
    }
    if (!updateData) {
      throw new Error("No updateData function provided");
    }
    if (typeof updateData !== "function") {
      throw new TypeError(
        `updateData is of type ${typeof updateData} but should be a function`
      );
    }

    const updatedConfig = updateConfig(config);
    const url = transformConfigToUrl(updatedConfig);

    setLoading(true);
    fetchData({
      method: "GET",
      url: url.toString(),
    })
      .then((response) => {
        if (updateData) {
          setData((prevData) => {
            const updatedData = updateData(prevData, response.data);
            return updatedData;
          });
        }
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return { data, loading, error, fetchMore };
};

export default useSearchAPIRequest;
